import { CollectionTable, CollectionCards } from '../../app/collection-view.js';
import {
  reimbursementMethodLabels,
  type Reimbursement,
  type PaymentInstructions,
} from '@hue/contracts';
import { PaymentInstructionsFields, PaymentInstructionsDetails } from './payment-instructions.js';
import { useClosedPeriod } from '../../app/closed-period-dialog.js';
import { useFinancialClassification } from '../../app/financial-classification-field.js';
import {
  Button,
  CurrencyInput,
  Combobox,
  FormErrorSummary,
  DatePicker,
  DateRangePicker,
  IconButton,
  Input,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  StatusPill,
  Tooltip,
  formatCivilDate,
  formatCivilRange,
  formatCurrency,
  type CivilDate,
  type StatusTone,
  useSnackbar,
  scrollMotion,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownCircle,
  ArrowUp,
  ArrowUpCircle,
  Handshake,
  Info,
  PencilLine,
  WalletCards,
  Repeat2,
  Ban,
  FileDown,
  Paperclip,
} from 'lucide-react';
import { useDeferredValue, useId, useRef, useState } from 'react';
import {
  financeAccountsApi,
  financialObligationsApi,
  getApiError,
  getDuplicateSuspects,
  type DuplicateSuspect,
  type FinanceAccount,
  type FinancialObligation,
  type FinancialObligationDirection,
  type FinancialObligationInput,
  type FinancialObligationSort,
  type FinancialObligationStatusChoice,
} from '../../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
  SortBar,
  SortableColumn,
  SummaryStrip,
} from '../../app/data-view.js';
import { payablesViewDefaults, type PayablesView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { SettleObligationModal } from './company-reconciliation.js';
import { CompanyRecurrences, type RecurrenceCapabilities } from './company-recurrences.js';
import { DuplicateSuspicionDialog } from './duplicate-suspicion-dialog.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';
import { ReceiptsDialog } from '../../app/receipts-dialog.js';

export interface PayablesCapabilities {
  canClassify?: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canCancel: boolean;
  canSettle: boolean;
  recurrences: RecurrenceCapabilities;
}
/// Despesa e receita, nunca "conta": conta é a bancária e a do plano de contas. As duas palavras
/// são femininas, então os textos variam só no substantivo.
const wording = {
  PAYABLE: {
    title: 'Despesas',
    one: 'despesa',
    many: 'despesas',
    One: 'Despesa',
    open: 'A pagar',
    paid: 'Pagas',
  },
  RECEIVABLE: {
    title: 'Receitas',
    one: 'receita',
    many: 'receitas',
    One: 'Receita',
    open: 'A receber',
    paid: 'Recebidas',
  },
} as const satisfies Record<FinancialObligationDirection, Record<string, string>>;
/// Quatro situações sem sobreposição, para marcar quantas quiser. "Em aberto" não é uma opção a
/// mais: é o nome de A vencer e Vencidas marcadas juntas, o que falta quitar.
function statusOptionsOf(direction: FinancialObligationDirection) {
  return [
    { value: 'UPCOMING', label: 'A vencer' },
    { value: 'OVERDUE', label: 'Vencidas' },
    { value: 'PAID', label: wording[direction].paid },
    { value: 'CANCELED', label: 'Canceladas' },
  ] as const satisfies ReadonlyArray<{ value: FinancialObligationStatusChoice; label: string }>;
}
/// A ordem das opções, para a URL não mudar conforme a ordem em que se marcou.
function inOrder<T extends string>(chosen: readonly T[], options: ReadonlyArray<{ value: T }>) {
  return options.map((option) => option.value).filter((value) => chosen.includes(value));
}
function statusSummary(chosen: readonly FinancialObligationStatusChoice[]): string | undefined {
  if (chosen.length === 0 || chosen.length === 4) return 'Todas';
  if (chosen.length === 2 && chosen.includes('UPCOMING') && chosen.includes('OVERDUE'))
    return 'Em aberto';
  return undefined;
}
function columnsOf(
  direction: FinancialObligationDirection,
): Array<{ sort: FinancialObligationSort; label: string }> {
  return [
    { sort: 'description', label: wording[direction].One },
    { sort: 'dueOn', label: 'Vencimento' },
    { sort: 'competenceOn', label: 'Competência' },
    { sort: 'amount', label: 'Valor' },
  ];
}

/// Despesas ou Receitas: o mesmo compromisso financeiro, com o sentido fixado pela tela.
export function CompanyPayables({
  direction,
  capabilities,
  unitNames,
  today,
  timeZone,
  view,
  onView,
}: {
  direction: FinancialObligationDirection;
  capabilities: PayablesCapabilities;
  /// Presente só na visão consolidada (ADR 0039): o nome de cada unidade alcançada, para a linha
  /// dizer de onde é o compromisso. Ausente, a tela é de uma unidade e não repete o óbvio.
  unitNames?: ReadonlyMap<string, string> | undefined;
  today: CivilDate;
  /// Fuso da empresa, para a data em que cada comprovante foi anexado.
  timeZone: string;
  view: PayablesView;
  onView: ViewSetter<PayablesView>;
}) {
  const words = wording[direction];
  const statusOptions = statusOptionsOf(direction);
  const columns = columnsOf(direction);
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const period = useOperationalPeriod(today);
  const [viewing, setViewing] = useState<FinancialObligation>();
  const [attachmentsOf, setAttachmentsOf] = useState<FinancialObligation>();
  // Quem cadastra despesa já paga e anexa primeiro ainda precisa chegar à conciliação prometida.
  const [settleAfterAttachments, setSettleAfterAttachments] = useState(false);
  const [editing, setEditing] = useState<FinancialObligation | null>();
  const [canceling, setCanceling] = useState<FinancialObligation>();
  const [settling, setSettling] = useState<FinancialObligation>();
  const [recurrencesOpen, setRecurrencesOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [counterpartySearch, setCounterpartySearch] = useState('');
  const deferredCounterpartySearch = useDeferredValue(counterpartySearch.trim());
  const chosenCounterparties = view.counterparty;
  /// Os nomes vêm do servidor enquanto a pessoa digita: o cliente de uma receita não tem teto.
  const counterparties = useQuery({
    queryKey: [
      'company',
      'financial-obligations',
      'counterparties',
      direction,
      deferredCounterpartySearch,
    ],
    queryFn: () =>
      financialObligationsApi.counterparties({
        direction,
        ...(deferredCounterpartySearch ? { search: deferredCounterpartySearch } : {}),
        limit: 20,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const counterpartyOptions = [
    ...chosenCounterparties.map((name) => ({ value: name, label: name })),
    ...(counterparties.data ?? [])
      .filter((item) => !chosenCounterparties.includes(item.name))
      .map((item) => ({ value: item.name, label: `${item.name} (${String(item.count)})` })),
  ];
  const accounts = useQuery({
    queryKey: ['company', 'finance-accounts', 'active'],
    queryFn: () =>
      financeAccountsApi.list({
        activity: ['ACTIVE'],
        sort: 'displayName',
        direction: 'asc',
      }),
    retry: false,
  });
  /// O recorte de dias manda no mês de trabalho enquanto existir, e vale sobre a mesma data que a
  /// referência escolhida — vencimento ou competência —, para o recorte e o mês nunca falarem de
  /// datas diferentes. Sem recorte, o mês da barra continua valendo.
  const ranged = Boolean(view.from ?? view.to);
  const from = ranged ? view.from : period.from;
  const to = ranged ? view.to : period.to;
  const filters = {
    ...(view.origin.length ? { origins: view.origin } : {}),
    today,
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    ...(chosenCounterparties.length ? { counterparties: chosenCounterparties } : {}),
    direction,
    ...(view.status.length ? { statuses: view.status } : {}),
    ...(view.account.length ? { financeAccountIds: view.account } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    periodBy: view.periodBy,
    sort: view.sort,
    order: view.direction,
    limit: view.limit,
    offset: view.offset,
  };
  const page = useQuery({
    queryKey: ['company', 'financial-obligations', filters],
    queryFn: () => financialObligationsApi.list(filters),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const previousTo = from
    ? new Date(new Date(`${from}T00:00:00Z`).getTime() - 86400000).toISOString().slice(0, 10)
    : undefined;
  const previous = useQuery({
    queryKey: [
      'company',
      'financial-obligations',
      'previous',
      today,
      from,
      view.account,
      view.origin,
      chosenCounterparties,
    ],
    queryFn: () =>
      financialObligationsApi.list({
        today,
        direction: 'PAYABLE',
        statuses: ['OVERDUE'],
        ...(view.origin.length ? { origins: view.origin } : {}),
        ...(chosenCounterparties.length ? { counterparties: chosenCounterparties } : {}),
        ...(view.account.length ? { financeAccountIds: view.account } : {}),
        to: previousTo!,
        periodBy: 'dueOn',
        sort: 'dueOn',
        order: 'asc',
        limit: 1,
        offset: 0,
      }),
    enabled: direction === 'PAYABLE' && !ranged && Boolean(from),
    retry: false,
  });
  const total = page.data?.total ?? 0;
  async function exportReport() {
    if (exporting) return;
    setExporting(true);
    try {
      const blob = await financialObligationsApi.export({ ...filters, offset: 0 });
      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${direction === 'PAYABLE' ? 'despesas' : 'receitas'}-${today}.pdf`;
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
      snackbar.success('Relatório exportado com o resultado completo dos filtros.');
    } catch (error) {
      snackbar.failure(getApiError(error));
    } finally {
      setExporting(false);
    }
  }
  function toggleSort(sort: FinancialObligationSort) {
    onView(
      view.sort === sort
        ? { direction: view.direction === 'asc' ? 'desc' : 'asc' }
        : { sort, direction: sort === 'description' ? 'asc' : 'desc' },
    );
  }
  async function refreshed(message: string) {
    await queryClient.invalidateQueries({ queryKey: ['company', 'financial-obligations'] });
    await queryClient.invalidateQueries({ queryKey: ['company', 'reconciliation'] });
    setEditing(undefined);
    setCanceling(undefined);
    snackbar.success(message);
  }
  /// Uma conta da URL que saiu do alcance continua na lista, para poder ser desmarcada.
  const accountOptions = [
    ...(accounts.data ?? []).map((item) => ({ value: item.id, label: item.displayName })),
    ...view.account
      .filter((id) => !(accounts.data ?? []).some((item) => item.id === id))
      .map((id) => ({ value: id, label: 'Conta financeira indisponível' })),
  ];
  const originOptions = [
    ...(page.data?.origins ?? []),
    ...view.origin
      .filter((value) => !(page.data?.origins ?? []).some((item) => item.value === value))
      .map((value) => ({ value, label: 'Origem indisponível' })),
  ];
  /// Uma etiqueta por escolha, que se remove sozinha; a Situação no padrão (Em aberto) não aparece.
  const each = (
    key: 'origin' | 'counterparty' | 'account',
    chosen: readonly string[],
    labelOf: (value: string) => string,
  ) =>
    chosen.map((value) => ({
      key: `${key}:${value}`,
      label: labelOf(value),
      onRemove: () => onView({ [key]: chosen.filter((one) => one !== value) }),
    }));
  const defaultStatus = statusSummary(view.status) === 'Em aberto';
  const chips = [
    ...each(
      'origin',
      view.origin,
      (value) => originOptions.find((one) => one.value === value)!.label,
    ),
    ...(defaultStatus
      ? []
      : [
          {
            key: 'status',
            label:
              statusSummary(view.status) === 'Todas'
                ? 'Todas as situações'
                : statusOptions
                    .filter((one) => view.status.includes(one.value))
                    .map((one) => one.label)
                    .join(', '),
            onRemove: () => onView({ status: payablesViewDefaults.status }),
          },
        ]),
    ...each('counterparty', chosenCounterparties, (value) => value),
    ...each(
      'account',
      view.account,
      (value) => accountOptions.find((one) => one.value === value)!.label,
    ),
    ...(view.periodBy === 'competenceOn'
      ? [
          {
            key: 'periodBy',
            label: 'Referência: competência',
            onRemove: () => onView({ periodBy: 'dueOn' }),
          },
        ]
      : []),
    ...(ranged
      ? [
          {
            key: 'range',
            label: formatCivilRange({ from: view.from, to: view.to }, 'Mês de trabalho'),
            onRemove: () => onView({ from: null, to: null }),
          },
        ]
      : []),
  ];
  return (
    <section aria-labelledby="payables-title">
      <PageHeader
        trail={`Financeiro / ${words.title}`}
        id="payables-title"
        title={words.title}
        description={
          <>
            {direction === 'PAYABLE'
              ? 'Despesas próprias e de outros módulos, reunidas sem duplicar a fonte original.'
              : 'Receitas próprias e de outros módulos, reunidas sem duplicar a fonte original.'}
            <Tooltip content="Despesas e receitas de outros módulos são consultadas aqui. Altere o cadastro no módulo de origem; a conciliação dá baixa nos dois lugares.">
              <button
                className="ml-1 inline-flex cursor-pointer align-middle text-content-faint"
                aria-label={`Como funcionam as ${words.many} integradas`}
              >
                <Info className="size-4" />
              </button>
            </Tooltip>
          </>
        }
        actions={
          <>
            {/* Recorrência é só de despesa: aluguel, água, energia, tributos. */}
            {direction === 'PAYABLE' && capabilities.recurrences.canRead ? (
              <Button variant="outline" onClick={() => setRecurrencesOpen(true)}>
                <Repeat2 className="size-4" />
                Recorrências
              </Button>
            ) : null}
            {capabilities.canCreate ? (
              <CreateButton onClick={() => setEditing(null)}>Nova {words.one}</CreateButton>
            ) : null}
          </>
        }
      />
      {direction === 'PAYABLE' && !ranged && from && (previous.data?.total ?? 0) > 0 ? (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-warning bg-surface-soft p-4"
        >
          <p>Despesas vencidas de meses anteriores: {previous.data!.total}.</p>
          <Button
            variant="outline"
            onClick={() =>
              onView({
                from: undefined,
                to: previousTo,
                periodBy: 'dueOn',
                status: ['OVERDUE'],
                offset: 0,
              })
            }
          >
            Ver pendências anteriores
          </Button>
        </div>
      ) : null}
      {unitNames ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-surface-border bg-surface-card p-4 text-sm text-content-secondary"
        >
          Visão consolidada: {words.many} de todas as suas unidades, só para consulta. Para lançar,
          alterar ou conciliar, escolha uma unidade no seletor acima.
        </p>
      ) : null}
      {page.data?.summary
        ? page.data.summary.map((summary) => (
            <div className="mb-5" key={summary.currency}>
              <SummaryStrip
                stackOnMobile
                label={`Compromissos completos em ${summary.currency}`}
                items={[
                  {
                    label:
                      direction === 'PAYABLE'
                        ? 'Total comprado / investido'
                        : 'Total dos compromissos',
                    value: formatCurrency(summary.familyAmountCents, summary.currency),
                  },
                  {
                    label: direction === 'PAYABLE' ? 'Já pago / reembolsado' : 'Já recebido',
                    value: formatCurrency(summary.familyPaidCents, summary.currency),
                    tone: 'positive',
                  },
                  {
                    label: direction === 'PAYABLE' ? 'Falta quitar' : 'Falta receber',
                    value: formatCurrency(summary.familyOutstandingCents, summary.currency),
                    tone: 'warning',
                  },
                ]}
              />
              <p className="mt-2 text-xs text-content-muted">
                Valores completos dos compromissos encontrados, incluindo as demais parcelas. Cada
                compromisso e credor conta uma vez; parcelas canceladas não somam.
              </p>
              <div className="mt-3">
                <SummaryStrip
                  stackOnMobile
                  label={`Parcelas do período em ${summary.currency}`}
                  items={[
                    {
                      label: 'Valor das parcelas',
                      value: formatCurrency(summary.amountCents, summary.currency),
                    },
                    {
                      label:
                        direction === 'PAYABLE' ? 'Pago nas parcelas' : 'Recebido nas parcelas',
                      value: formatCurrency(summary.paidCents, summary.currency),
                      tone: 'positive',
                    },
                    {
                      label: words.open,
                      value: formatCurrency(summary.outstandingCents, summary.currency),
                      tone: 'warning',
                    },
                  ]}
                />
              </div>
            </div>
          ))
        : page.data?.totals.map((totalByCurrency) => (
            <div className="mb-3" key={totalByCurrency.currency}>
              <SummaryStrip
                label={`Resumo das ${words.many} em ${totalByCurrency.currency}`}
                items={[
                  direction === 'PAYABLE'
                    ? {
                        label: words.open,
                        value: formatCurrency(
                          totalByCurrency.payableCents,
                          totalByCurrency.currency,
                        ),
                        tone: 'warning',
                      }
                    : {
                        label: words.open,
                        value: formatCurrency(
                          totalByCurrency.receivableCents,
                          totalByCurrency.currency,
                        ),
                        tone: 'positive',
                      },
                ]}
              />
            </div>
          ))}
      <FilterBar
        layout="stacked"
        searchId="obligation-search"
        searchLabel={`Buscar ${words.one}`}
        searchPlaceholder="Contraparte ou descrição"
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() =>
          onView({
            status: payablesViewDefaults.status,
            origin: [],
            counterparty: [],
            account: [],
            periodBy: 'dueOn',
            from: null,
            to: null,
          })
        }
        count={`${total} ${total === 1 ? words.one : words.many}`}
        resultsLabel={`Ver ${total} ${words.many}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit })}
        countActions={
          <IconButton
            label={`Exportar ${words.many} filtradas em PDF`}
            icon={<FileDown />}
            loading={exporting}
            disabled={page.isFetching || page.isPlaceholderData || !total || Boolean(page.error)}
            onClick={() => void exportReport()}
          />
        }
      >
        <MultiSelect
          label="Origem"
          itemNoun="origens"
          allLabel="Todas as origens"
          noneLabel="Todas as origens"
          searchPlaceholder="Buscar origem"
          options={originOptions}
          selected={view.origin}
          onChange={(origin) => onView({ origin: inOrder(origin, originOptions) })}
        />
        <MultiSelect
          label="Situação"
          itemNoun="situações"
          summary={statusSummary(view.status)}
          searchPlaceholder="Buscar situação"
          options={statusOptions}
          selected={view.status}
          onChange={(status) =>
            onView({
              status: inOrder(status as FinancialObligationStatusChoice[], statusOptions),
            })
          }
        />
        <MultiSelect
          label="Contraparte"
          itemNoun="contrapartes"
          noneLabel="Todas as contrapartes"
          searchPlaceholder="Buscar contraparte"
          options={counterpartyOptions}
          selected={chosenCounterparties}
          onChange={(counterparty) => onView({ counterparty })}
          onSearchChange={setCounterpartySearch}
          loading={counterparties.isFetching && !counterparties.data}
        />
        {/* Vencimento ou competência diz como ler o mês, não recorta registros: escolha única. */}
        <div className="min-w-0">
          <span className="mb-2 block text-xs font-semibold text-content/70">
            Referência do mês
          </span>
          <SegmentedControl
            label="Referência do mês"
            compact
            options={[
              { value: 'dueOn', label: 'Vencimento' },
              { value: 'competenceOn', label: 'Competência' },
            ]}
            value={view.periodBy}
            onChange={(periodBy) => onView({ periodBy })}
          />
        </div>
        <MultiSelect
          label="Conta prevista"
          itemNoun="contas"
          allLabel="Todas as contas"
          noneLabel="Todas as contas"
          searchPlaceholder="Buscar conta"
          options={accountOptions}
          selected={view.account}
          onChange={(account) => onView({ account: inOrder(account, accountOptions) })}
        />
        <div className="min-w-0">
          <span className="mb-1.5 block text-xs font-semibold text-content/70">Dias</span>
          <DateRangePicker
            label={`Período das ${words.many}`}
            value={{ from: view.from, to: view.to }}
            onValueChange={(next) => onView({ from: next.from, to: next.to })}
            today={today}
            emptyLabel="Mês de trabalho"
          />
        </div>
      </FilterBar>
      {page.isLoading ? (
        <LoadingCard label={`Carregando ${words.many}`} />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={WalletCards}
          title={`Nenhuma ${words.one} encontrada`}
          description={`Ajuste os filtros ou cadastre a primeira ${words.one}.`}
        />
      ) : (
        <div className="hue-responsive-collection overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          <CollectionCards>
            <div className="hue-collection-cards">
              <SortBar
                label={`Ordenação das ${words.many}`}
                columns={columns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {page.data?.obligations.map((item) => (
                  <li key={item.id}>
                    <ObligationCard
                      item={item}
                      unitName={unitNames?.get(item.businessUnitId)}
                      capabilities={capabilities}
                      onView={() => setViewing(item)}
                      onEdit={() => setEditing(item)}
                      onCancel={() => setCanceling(item)}
                      onSettle={() => setSettling(item)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </CollectionCards>
          <CollectionTable>
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">{words.title}</caption>
              <colgroup>
                <col className="w-[36%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
                <col className="w-[22%]" />
                <col className="w-[18%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-surface-border">
                  {columns.map((column) => (
                    <SortableColumn
                      key={column.sort}
                      column={column.sort}
                      label={column.label}
                      sort={view.sort}
                      direction={view.direction}
                      onToggle={toggleSort}
                    />
                  ))}
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {page.data?.obligations.map((item) => (
                  <tr key={item.id}>
                    <td className="px-5 py-4">
                      <button
                        type="button"
                        className="block max-w-full cursor-pointer truncate text-left text-sm font-semibold text-content underline-offset-4 hover:text-brand-400 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        onClick={() => setViewing(item)}
                        aria-label={`Ver detalhes de ${item.description}`}
                      >
                        {item.description}
                      </button>
                      <span className="block truncate text-xs text-content-muted">
                        {item.counterpartyName}
                      </span>
                      {unitNames ? (
                        <span className="block truncate text-xs text-content-faint">
                          {unitNames.get(item.businessUnitId)}
                        </span>
                      ) : null}
                      <ObligationStatus item={item} />
                    </td>
                    <td className="px-3 py-4 text-sm">{formatCivilDate(item.dueOn)}</td>
                    <td className="px-3 py-4 text-sm">{formatCivilDate(item.competenceOn)}</td>
                    <td className="px-3 py-4 text-sm font-semibold tabular-nums">
                      {formatCurrency(item.amountCents, item.currency)}
                      <ObligationAmounts item={item} />
                    </td>
                    <td className="px-1 py-4">
                      <Actions
                        item={item}
                        capabilities={capabilities}
                        onEdit={() => setEditing(item)}
                        onCancel={() => setCanceling(item)}
                        onSettle={() => setSettling(item)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollectionTable>
          <Pagination
            total={total}
            limit={view.limit}
            offset={view.offset}
            onOffsetChange={(offset) => onView({ offset })}
            itemLabel={(count) => (count === 1 ? words.one : words.many)}
          />
        </div>
      )}
      {viewing ? (
        <ObligationDetails
          item={viewing}
          onClose={() => setViewing(undefined)}
          onAttachments={() => {
            setAttachmentsOf(viewing);
            setViewing(undefined);
          }}
        />
      ) : null}
      {attachmentsOf ? (
        <ReceiptsDialog
          source={{
            queryKey: ['company', 'financial-obligations', 'attachments', attachmentsOf.id],
            list: () => financialObligationsApi.attachments(attachmentsOf.id),
            attach: (file) => financialObligationsApi.attach(attachmentsOf.id, file),
            remove: financialObligationsApi.removeAttachment,
            fileUrl: financialObligationsApi.attachmentFileUrl,
          }}
          description={`${attachmentsOf.description} · anexe ${attachmentsOf.direction === 'PAYABLE' ? 'nota fiscal, cupom, comprovante da compra, do pagamento ou do reembolso' : 'nota fiscal, recibo ou comprovante do recebimento'}. Até 20 arquivos, com 10 MB cada.`}
          emptyText={`Nenhum comprovante anexado a esta ${wording[attachmentsOf.direction].one}.`}
          timeZone={timeZone}
          // Cancelada fica só para consulta; a visão consolidada já chega sem poder editar.
          canManage={capabilities.canUpdate && attachmentsOf.status !== 'CANCELED'}
          onClose={() => {
            if (settleAfterAttachments) setSettling(attachmentsOf);
            setSettleAfterAttachments(false);
            setAttachmentsOf(undefined);
          }}
        />
      ) : null}
      {editing !== undefined ? (
        <ObligationDialog
          canClassify={capabilities.canClassify}
          canAttach={capabilities.canUpdate}
          canSettle={capabilities.canSettle}
          direction={direction}
          item={editing}
          accounts={accounts.data ?? []}
          today={today}
          onClose={() => setEditing(undefined)}
          onSaved={async (saved, next) => {
            await refreshed(`${words.One} ${editing ? 'atualizada' : 'cadastrada'}.`);
            if (next === 'attachments' || next === 'attachments-then-settle') {
              setSettleAfterAttachments(next === 'attachments-then-settle');
              setAttachmentsOf(saved);
            } else if (next === 'settle') setSettling(saved);
          }}
        />
      ) : null}
      {canceling ? (
        <CancellationDialog
          item={canceling}
          onClose={() => setCanceling(undefined)}
          onSaved={() => refreshed(`${words.One} cancelada.`)}
        />
      ) : null}
      {settling ? (
        <SettleObligationModal
          target={settling.target}
          title={settling.counterpartyName}
          description={settling.description}
          outstandingCents={settling.outstandingCents}
          currency={settling.currency}
          direction={settling.direction}
          onClose={() => setSettling(undefined)}
          onSettled={async (message) => {
            setSettling(undefined);
            await refreshed(message);
          }}
        />
      ) : null}
      {recurrencesOpen ? (
        <CompanyRecurrences
          month={period.from ?? today}
          capabilities={capabilities.recurrences}
          onClose={() => setRecurrencesOpen(false)}
        />
      ) : null}
    </section>
  );
}

function ObligationAmounts({ item }: { item: FinancialObligation }) {
  const paid =
    item.status === 'CANCELED' ? 0 : Math.max(item.amountCents - item.outstandingCents, 0);
  return (
    <span className="mt-1 block text-xs font-normal text-content-muted">
      <span className="block whitespace-nowrap">
        {item.direction === 'PAYABLE' ? 'Pago' : 'Recebido'}: {formatCurrency(paid, item.currency)}
      </span>
      <span className="block whitespace-nowrap">
        Em aberto: {formatCurrency(item.outstandingCents, item.currency)}
      </span>
      <span className="block whitespace-nowrap">
        Total: {formatCurrency(item.familyTotals?.amountCents ?? item.amountCents, item.currency)}
      </span>
    </span>
  );
}

/// Uma situação por compromisso, com a origem em pílula própria.
function ObligationStatus({ item }: { item: FinancialObligation }) {
  const payable = item.direction === 'PAYABLE';
  const situation: { tone: StatusTone; label: string } =
    item.status === 'CANCELED'
      ? { tone: 'neutral', label: 'Cancelada' }
      : item.status === 'PAID'
        ? { tone: 'positive', label: payable ? 'Paga' : 'Recebida' }
        : item.status === 'OVERDUE'
          ? { tone: 'danger', label: payable ? 'Vencida a pagar' : 'Vencida a receber' }
          : { tone: payable ? 'warning' : 'positive', label: payable ? 'A pagar' : 'A receber' };
  return (
    <div className="mt-2 flex gap-2">
      <StatusPill tone={situation.tone}>{situation.label}</StatusPill>
      <StatusPill tone="neutral">{item.sourceLabel}</StatusPill>
    </div>
  );
}
function Actions({
  item,
  capabilities,
  onEdit,
  onCancel,
  onSettle,
}: {
  item: FinancialObligation;
  capabilities: PayablesCapabilities;
  onEdit: () => void;
  onCancel: () => void;
  onSettle: () => void;
}) {
  if (item.status === 'CANCELED') return null;
  // Conciliar vale para qualquer compromisso em aberto, inclusive o que outro módulo publicou:
  // editar é que continua sendo só de quem é dono do dado (ADR 0021).
  const settleable = capabilities.canSettle && item.outstandingCents > 0;
  if (!item.editable && !settleable) return null;
  const { one } = wording[item.direction];
  return (
    <div className="flex justify-end gap-1">
      {settleable ? (
        <IconButton
          label={`Conciliar ${one}`}
          icon={<Handshake className="size-4" />}
          onClick={onSettle}
        />
      ) : null}
      {item.editable && capabilities.canUpdate ? (
        <IconButton
          label={`Editar ${one}`}
          icon={<PencilLine className="size-4" />}
          onClick={onEdit}
        />
      ) : null}
      {item.editable && capabilities.canCancel ? (
        <IconButton
          label={`Cancelar ${one}`}
          tone="danger"
          icon={<Ban className="size-4" />}
          onClick={onCancel}
        />
      ) : null}
    </div>
  );
}
function ObligationCard(
  props: Parameters<typeof Actions>[0] & {
    item: FinancialObligation;
    unitName?: string | undefined;
    onView: () => void;
  },
) {
  const { item, unitName, onView } = props;
  return (
    <div className="p-4">
      <div className="flex items-start gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
          {item.direction === 'PAYABLE' ? (
            <ArrowDownCircle className="size-5" />
          ) : (
            <ArrowUpCircle className="size-5" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="block max-w-full cursor-pointer truncate text-left text-sm font-semibold text-content underline-offset-4 hover:text-brand-400 hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            onClick={onView}
            aria-label={`Ver detalhes de ${item.description}`}
          >
            {item.description}
          </button>
          <span className="block truncate text-xs text-content-muted">{item.counterpartyName}</span>
          {unitName ? (
            <span className="block truncate text-xs text-content-faint">{unitName}</span>
          ) : null}
          <p className="mt-2 text-sm font-semibold">
            {formatCurrency(item.amountCents, item.currency)} · {formatCivilDate(item.dueOn)}
          </p>
          <ObligationAmounts item={item} />
          <ObligationStatus item={item} />
        </div>
        <Actions {...props} />
      </div>
    </div>
  );
}

function ObligationDetails({
  item,
  onClose,
  onAttachments,
}: {
  item: FinancialObligation;
  onClose: () => void;
  onAttachments: () => void;
}) {
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={`Detalhes da ${wording[item.direction].one}`}
      footer={
        <>
          {/* Comprovante é do cadastro próprio do Financeiro; o que outro módulo publicou guarda
              os seus arquivos lá, como a compra do Investimento. */}
          {item.editable ? (
            <Button variant="outline" onClick={onAttachments}>
              <Paperclip className="size-4" />
              Comprovantes
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </>
      }
    >
      <dl className="grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Detail label={wording[item.direction].One} value={item.description} />
        </div>
        <Detail
          label={
            item.reimbursement
              ? 'Pessoa a reembolsar'
              : counterpartyLabel(
                  item.direction,
                  item.status === 'CANCELED'
                    ? 'CANCELED'
                    : item.outstandingCents === 0
                      ? 'PAID'
                      : 'PENDING',
                )
          }
          value={item.counterpartyName}
        />
        {item.reimbursement ? (
          <>
            <Detail label="Fornecedor da compra" value={item.reimbursement.supplierName} />
            <Detail
              label="Data da compra"
              value={formatCivilDate(item.reimbursement.purchasedOn)}
            />
            <Detail
              label="Meio usado na compra"
              value={reimbursementMethodLabels[item.reimbursement.purchaseMethod]}
            />
          </>
        ) : null}
        <Detail label="Vencimento" value={formatCivilDate(item.dueOn)} />
        <Detail label="Competência" value={formatCivilDate(item.competenceOn)} />
        <Detail label="Valor" value={formatCurrency(item.amountCents, item.currency)} />
        <Detail label="Em aberto" value={formatCurrency(item.outstandingCents, item.currency)} />
        <Detail
          label={item.direction === 'PAYABLE' ? 'Pago na parcela' : 'Recebido na parcela'}
          value={formatCurrency(
            item.status === 'CANCELED' ? 0 : Math.max(item.amountCents - item.outstandingCents, 0),
            item.currency,
          )}
        />
        <Detail
          label="Total do compromisso"
          value={formatCurrency(item.familyTotals?.amountCents ?? item.amountCents, item.currency)}
        />
        {item.cardBreakdown ? (
          <>
            <Detail
              label="Bruto da parcela"
              value={formatCurrency(item.cardBreakdown.grossCents, item.currency)}
            />
            <Detail
              label="Taxa do cartão"
              value={formatCurrency(item.cardBreakdown.cardFeeCents, item.currency)}
            />
            <Detail
              label="Custo da antecipação"
              value={formatCurrency(item.cardBreakdown.anticipationFeeCents, item.currency)}
            />
          </>
        ) : null}
        <div className="sm:col-span-2">
          <span className="mb-2 block text-xs font-medium text-content-muted">Situação</span>
          <ObligationStatus item={item} />
        </div>
      </dl>
      <PaymentInstructionsDetails value={item.paymentInstructions} />
    </Modal>
  );
}

/// O rótulo acompanha o momento do pagamento: pagar depois, já pago ou cancelado.
function counterpartyLabel(
  direction: FinancialObligationDirection,
  moment: 'PENDING' | 'PAID' | 'CANCELED',
) {
  const labels = {
    PAYABLE: {
      PENDING: 'Quem vai receber o pagamento',
      PAID: 'Quem recebeu o pagamento',
      CANCELED: 'Quem receberia o pagamento',
    },
    RECEIVABLE: {
      PENDING: 'Quem vai fazer o pagamento',
      PAID: 'Quem fez o pagamento',
      CANCELED: 'Quem faria o pagamento',
    },
  } as const;
  return labels[direction][moment];
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium text-content-muted">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-content">{value}</dd>
    </div>
  );
}

function ObligationDialog({
  direction: screenDirection,
  item,
  canClassify = false,
  canAttach,
  canSettle,
  accounts,
  today,
  onClose,
  onSaved,
}: {
  direction: FinancialObligationDirection;
  canClassify?: boolean | undefined;
  canAttach: boolean;
  canSettle: boolean;
  item: FinancialObligation | null;
  accounts: FinanceAccount[];
  today: CivilDate;
  onClose: () => void;
  onSaved: (
    saved: FinancialObligation,
    next?: 'attachments' | 'attachments-then-settle' | 'settle',
  ) => Promise<void>;
}) {
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [attempted, setAttempted] = useState(false);
  const direction = item?.direction ?? screenDirection;
  const classification = useFinancialClassification(
    canClassify,
    'OBLIGATION',
    item?.id,
    attempted,
    direction,
  );
  const [accountId, setAccountId] = useState(item?.financeAccountId ?? '');
  const words = wording[direction];
  const [operation, setOperation] = useState<'PENDING' | 'PERSONAL' | 'COMPANY'>(
    item?.reimbursement ? 'PERSONAL' : 'PENDING',
  );
  const personal = direction === 'PAYABLE' && operation === 'PERSONAL';
  const alreadyPaid = operation === 'COMPANY';
  const payeeLabel = counterpartyLabel(
    direction,
    alreadyPaid || item?.outstandingCents === 0 ? 'PAID' : 'PENDING',
  );
  const [supplier, setSupplier] = useState(item?.reimbursement?.supplierName ?? '');
  const [purchasedOn, setPurchasedOn] = useState<CivilDate | null>(
    item?.reimbursement?.purchasedOn ?? today,
  );
  const [purchaseMethod, setPurchaseMethod] = useState<Reimbursement['purchaseMethod']>(
    item?.reimbursement?.purchaseMethod ?? 'PERSONAL_CARD',
  );
  const [paymentInstructions, setPaymentInstructions] = useState<PaymentInstructions | null>(
    item?.paymentInstructions ?? null,
  );
  const [counterparty, setCounterparty] = useState(item?.counterpartyName ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [competence, setCompetence] = useState<CivilDate | null>(item?.competenceOn ?? today);
  const [due, setDue] = useState<CivilDate | null>(item?.dueOn ?? today);
  const [amount, setAmount] = useState<number | null>(item?.amountCents ?? null);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [suspects, setSuspects] = useState<DuplicateSuspect[]>([]);
  const nextAction = useRef<'attachments' | 'attachments-then-settle' | 'settle' | undefined>(
    undefined,
  );
  const closedPeriod = useClosedPeriod();
  const account = accounts.find((one) => one.id === accountId);
  const [chosenCurrency, setChosenCurrency] = useState(item?.currency ?? 'BRL');
  const currency = account?.currency ?? chosenCurrency;
  // Quitada sem dados guardados não tem o que consultar; com dados, eles continuam visíveis.
  const showsPaymentData =
    direction === 'PAYABLE' &&
    !alreadyPaid &&
    !(item?.outstandingCents === 0 && !paymentInstructions);
  const accountChoices = [
    { value: '', label: 'Ainda não sei qual conta financeira usar' },
    ...accounts.map((one) => ({ value: one.id, label: one.displayName })),
  ];
  function inputOf(): FinancialObligationInput | undefined {
    if (
      !competence ||
      !due ||
      !amount ||
      counterparty.trim().length < 2 ||
      description.trim().length < 2 ||
      (personal && (supplier.trim().length < 2 || !purchasedOn))
    )
      return undefined;
    return {
      ...classification.options,
      paymentInstructions,
      reimbursement: personal
        ? { supplierName: supplier, purchasedOn: purchasedOn!, purchaseMethod }
        : null,
      financeAccountId: accountId || null,
      direction,
      counterpartyName: counterparty,
      description,
      competenceOn: competence,
      dueOn: due,
      amountCents: amount,
      currency,
    };
  }
  async function submit(input: FinancialObligationInput, duplicateReason?: string) {
    const saved = item
      ? await financialObligationsApi.update(item.id, input)
      : await financialObligationsApi.create(input, duplicateReason);
    await onSaved(saved, nextAction.current);
  }
  async function save(next?: 'attachments') {
    if (saving) return;
    setAttempted(true);
    setError(undefined);
    if (!classification.ready) return;
    const input = inputOf();
    if (!input) return;
    const settles = alreadyPaid && canSettle;
    nextAction.current = next
      ? settles
        ? 'attachments-then-settle'
        : next
      : settles
        ? 'settle'
        : undefined;
    setSaving(true);
    try {
      await submit(input);
    } catch (cause) {
      const found = getDuplicateSuspects(cause);
      if (found.length > 0) setSuspects(found);
      else if (
        !closedPeriod.intercept(
          cause,
          (override) => submit({ ...input, closedPeriodAdjustment: override }),
          () => undefined,
        )
      )
        setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={item ? `Editar ${words.one}` : `Nova ${words.one}`}
      description="Todos os campos são obrigatórios, exceto os marcados como opcionais."
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <FormErrorSummary form={formId} />
          {canAttach ? (
            <Button
              className="w-full px-3 sm:w-auto sm:px-6"
              variant="outline"
              type="submit"
              form={formId}
              name="next"
              value="attachments"
              loading={saving}
            >
              Salvar e anexar comprovantes
            </Button>
          ) : null}
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
            <Button className="px-3 sm:px-6" variant="outline" onClick={onClose}>
              Voltar
            </Button>
            <Button className="px-3 sm:px-6" type="submit" form={formId} loading={saving}>
              {alreadyPaid
                ? item
                  ? 'Salvar e conciliar'
                  : 'Cadastrar e conciliar'
                : item
                  ? 'Salvar alterações'
                  : `Cadastrar ${words.one}`}
            </Button>
          </div>
        </div>
      }
    >
      <form
        ref={formRef}
        className="min-w-0"
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const submitter = event.nativeEvent.submitter as HTMLButtonElement | null;
          void save(submitter?.value === 'attachments' ? 'attachments' : undefined);
        }}
      >
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          {direction === 'PAYABLE' ? (
            <div className="sm:col-span-2">
              <Select
                label="Como esta despesa foi paga?"
                value={operation}
                onValueChange={(value) => {
                  setOperation(value as typeof operation);
                  // Já paga: a previsão de conta e os dados para pagar deixam de valer no cadastro.
                  if (value === 'COMPANY' && !item) {
                    setChosenCurrency(currency);
                    setAccountId('');
                    setPaymentInstructions(null);
                  }
                }}
                options={[
                  {
                    value: 'PENDING',
                    label: item
                      ? 'Pagamento pela empresa conforme conciliação'
                      : 'Ainda não foi paga',
                  },
                  { value: 'PERSONAL', label: 'Recursos pessoais — a empresa vai reembolsar' },
                  ...(canSettle
                    ? [
                        {
                          value: 'COMPANY',
                          label: 'Já foi paga pela empresa — vincular ao extrato',
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          ) : null}
          <Input
            label={personal ? 'Pessoa a reembolsar' : payeeLabel}
            value={counterparty}
            maxLength={160}
            error={
              attempted && counterparty.trim().length < 2
                ? personal
                  ? 'Informe o nome da pessoa a reembolsar.'
                  : `Informe ${payeeLabel.toLowerCase()}.`
                : undefined
            }
            hint={
              personal
                ? 'Quem usou o próprio dinheiro, mesmo sem acesso ao sistema: funcionário, sócio ou terceiro.'
                : undefined
            }
            onChange={(event) => setCounterparty(event.target.value)}
          />
          <Input
            label="Descrição"
            value={description}
            maxLength={240}
            error={
              attempted && description.trim().length < 2 ? `Descreva a ${words.one}.` : undefined
            }
            onChange={(event) => setDescription(event.target.value)}
          />
          {personal ? (
            <>
              <Input
                label="Fornecedor da compra"
                value={supplier}
                maxLength={160}
                error={
                  attempted && supplier.trim().length < 2
                    ? 'Informe onde a compra foi feita.'
                    : undefined
                }
                onChange={(event) => setSupplier(event.target.value)}
              />
              <DatePicker
                label="Data da compra"
                value={purchasedOn}
                onValueChange={setPurchasedOn}
                today={today}
                error={attempted && !purchasedOn ? 'Informe a data da compra.' : undefined}
              />
              <Select
                label="Meio usado na compra"
                value={purchaseMethod}
                onValueChange={(value) =>
                  setPurchaseMethod(value as Reimbursement['purchaseMethod'])
                }
                options={Object.entries(reimbursementMethodLabels).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            </>
          ) : null}
          <DatePicker
            label="Competência"
            value={competence}
            onValueChange={setCompetence}
            today={today}
            error={attempted && !competence ? 'Informe a competência.' : undefined}
            hint={`Período a que esta ${words.one} pertence, mesmo que seja ${direction === 'PAYABLE' ? 'paga' : 'recebida'} em outro mês.`}
          />
          <DatePicker
            label={personal ? 'Data prevista do reembolso' : 'Vencimento'}
            value={due}
            onValueChange={setDue}
            today={today}
            error={attempted && !due ? 'Informe a data prevista.' : undefined}
            hint={
              direction === 'RECEIVABLE'
                ? 'Data prevista para receber. O dia do recebimento real virá do extrato.'
                : personal
                  ? 'Data em que a empresa espera reembolsar a pessoa. O dia do pagamento real virá do extrato.'
                  : alreadyPaid
                    ? 'Data combinada para o pagamento. O dia em que foi pago virá do extrato.'
                    : 'Data prevista para pagar. O dia do pagamento real virá do extrato.'
            }
          />
          <CurrencyInput
            label={personal ? 'Valor a reembolsar' : 'Valor'}
            currency={currency}
            value={amount}
            onValueChange={setAmount}
            error={attempted && !amount ? 'Informe um valor positivo.' : undefined}
          />
          {alreadyPaid ? null : (
            <Combobox
              label={
                direction === 'PAYABLE'
                  ? 'Conta financeira prevista para pagamento (opcional)'
                  : 'Conta financeira prevista para recebimento (opcional)'
              }
              placeholder="Escolha quando souber"
              selectedLabel={accountChoices.find((one) => one.value === accountId)?.label ?? ''}
              options={accountChoices}
              onSelect={(choice) => {
                if (choice.kind === 'option') {
                  setChosenCurrency(
                    accounts.find((one) => one.id === choice.value)?.currency ?? currency,
                  );
                  setAccountId(choice.value);
                }
              }}
              onClear={() => {
                setChosenCurrency(currency);
                setAccountId('');
              }}
              hint="É o banco ou caixa da empresa. Escolher aqui cria apenas uma previsão; não movimenta dinheiro."
            />
          )}
          {!account ? (
            <Select
              label="Moeda"
              value={chosenCurrency}
              onValueChange={(value) => setChosenCurrency(value as typeof chosenCurrency)}
              options={['BRL', 'USD', 'ARS'].map((value) => ({ value, label: value }))}
            />
          ) : null}
          {classification.field}
        </div>
        {personal && showsPaymentData ? (
          <p className="mt-4 text-xs text-content-muted">
            Os dados abaixo são para a empresa reembolsar a pessoa informada. O meio usado na compra
            original já está registrado acima.
          </p>
        ) : null}
        {showsPaymentData ? (
          <PaymentInstructionsFields
            value={paymentInstructions}
            onChange={setPaymentInstructions}
          />
        ) : null}
        <section
          aria-label="Comprovantes"
          className="mt-5 min-w-0 space-y-2 rounded-xl border border-surface-border p-4"
        >
          <h3 className="text-sm font-semibold">Comprovantes (opcional)</h3>
          <p className="text-sm text-content-muted">
            {canAttach
              ? `${direction === 'PAYABLE' ? 'Nota fiscal, cupom e comprovante da compra, do pagamento ou do reembolso' : 'Nota fiscal, recibo e comprovante do recebimento'} ficam juntos. Para enviar agora, use “Salvar e anexar comprovantes”, no fim deste formulário. Para enviar depois, clique na descrição da ${words.one} na lista e em Comprovantes.`
              : 'Anexar comprovantes exige permissão para editar despesas e receitas.'}
          </p>
        </section>
        <p
          role="status"
          className="mt-4 rounded-xl border border-surface-border bg-surface-elevated p-3 text-sm text-content-secondary"
        >
          {personal
            ? item?.outstandingCents === 0
              ? `A compra já foi reembolsada para ${counterparty.trim() || 'a pessoa informada'}. Os pagamentos conciliados serão preservados. `
              : `A compra ficará pendente de reembolso para ${counterparty.trim() || 'a pessoa informada'}. `
            : alreadyPaid
              ? `Na próxima etapa, você escolhe no extrato o pagamento desta ${words.one}. `
              : item?.outstandingCents === 0
                ? `Esta ${words.one} já foi ${direction === 'PAYABLE' ? 'paga' : 'recebida'}. Os valores conciliados serão preservados. `
                : `Esta ${words.one} ficará em aberto até o ${direction === 'PAYABLE' ? 'pagamento' : 'recebimento'} ser conciliado com o extrato. `}
          {classification.chosen
            ? `A conta do plano escolhida vale para a competência ${competence ? formatCivilDate(competence) : 'selecionada'}. `
            : ''}
          Nenhuma movimentação de dinheiro será criada neste cadastro.
        </p>
        {error ? <RequestAlert message={error} /> : null}
        <Button
          type="button"
          variant="ghost"
          className="mt-3"
          onClick={() =>
            formRef.current?.parentElement?.scrollTo({ top: 0, behavior: scrollMotion() })
          }
        >
          <ArrowUp className="size-4" />
          Voltar ao topo do formulário
        </Button>
      </form>
      {suspects.length > 0 ? (
        <DuplicateSuspicionDialog
          suspects={suspects}
          title="Lançamento parecido nessa competência"
          description={`Pode ser a mesma ${words.one} cadastrada duas vezes. Explique por que as duas devem existir.`}
          confirmLabel="Cadastrar mesmo assim"
          onConfirm={async (reason) => {
            const input = inputOf();
            if (input) await submit(input, reason);
          }}
          onClose={() => setSuspects([])}
        />
      ) : null}
      {closedPeriod.dialog}
    </Modal>
  );
}
function CancellationDialog({
  item,
  onClose,
  onSaved,
}: {
  item: FinancialObligation;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const closedPeriod = useClosedPeriod();
  async function save() {
    if (reason.trim().length < 3) {
      setError('Informe o motivo do cancelamento.');
      return;
    }
    setSaving(true);
    try {
      await financialObligationsApi.cancel(item.id, reason);
      await onSaved();
    } catch (cause) {
      if (
        !closedPeriod.intercept(
          cause,
          (override) => financialObligationsApi.cancel(item.id, reason, override),
          onSaved,
        )
      )
        setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={onClose}
      title={`Cancelar ${wording[item.direction].one}`}
      description={`A ${wording[item.direction].one} permanece no histórico e deixa de compor os valores em aberto.`}
      footer={
        <Footer
          saving={saving}
          onClose={onClose}
          onSave={() => void save()}
          label={`Cancelar ${wording[item.direction].one}`}
        />
      }
    >
      <Input
        label="Motivo"
        value={reason}
        maxLength={240}
        onChange={(event) => setReason(event.target.value)}
      />
      {error ? <RequestAlert message={error} /> : null}
      {closedPeriod.dialog}
    </Modal>
  );
}
function Footer({
  saving,
  onClose,
  onSave,
  label,
}: {
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  label: string;
}) {
  return (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={onClose}>
        Voltar
      </Button>
      <Button loading={saving} onClick={onSave}>
        {label}
      </Button>
    </div>
  );
}
